import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SHRTLogo } from '@/components/SHRTLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, ArrowRight, CheckCircle, XCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';

export default function SetupPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { verifySetupToken, setupPassword } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      const result = await verifySetupToken(token);
      
      if (result.success) {
        setTokenValid(true);
        setTokenInfo(result.data);
      }
      
      setVerifying(false);
    };

    verifyToken();
  }, [token, verifySetupToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    if (password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    
    setLoading(true);
    
    const result = await setupPassword(token, password);
    
    if (result.success) {
      setSuccess(true);
      toast.success('Passwort erfolgreich eingerichtet!');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground uppercase tracking-wider text-sm">Token wird überprüft...</p>
        </div>
      </div>
    );
  }

  // No token or invalid token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <SHRTLogo size="md" className="mx-auto" />
          </div>
          <div className="bg-card border border-border p-8">
            <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h1 className="font-['Oswald'] text-2xl font-bold text-foreground uppercase tracking-tight mb-2">
              Ungültiger Link
            </h1>
            <p className="text-muted-foreground mb-6">
              Dieser Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen Link an.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full h-12 bg-primary hover:bg-red-700 text-white uppercase tracking-widest font-bold text-sm rounded-none"
              data-testid="back-to-login"
            >
              Zur Anmeldung
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <SHRTLogo size="md" className="mx-auto" />
          </div>
          <div className="bg-card border border-border p-8">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="font-['Oswald'] text-2xl font-bold text-foreground uppercase tracking-tight mb-2">
              Passwort eingerichtet
            </h1>
            <p className="text-muted-foreground mb-6">
              Ihr Passwort wurde erfolgreich eingerichtet. Sie können sich jetzt anmelden.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full h-12 bg-primary hover:bg-red-700 text-white uppercase tracking-widest font-bold text-sm rounded-none"
              data-testid="go-to-login"
            >
              Zur Anmeldung
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Setup form
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <div className="max-w-md w-full">
        <div className="mb-8 text-center">
          <SHRTLogo size="md" className="mx-auto mb-6" />
          <h1 className="font-['Oswald'] text-3xl font-bold text-foreground uppercase tracking-tight mb-2">
            Passwort einrichten
          </h1>
          {tokenInfo && (
            <p className="text-muted-foreground">
              Willkommen, <span className="text-foreground">{tokenInfo.name || tokenInfo.email}</span>
            </p>
          )}
        </div>

        <div className="bg-card border border-border p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">
                Neues Passwort
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mindestens 8 Zeichen"
                  className="pl-12 pr-12 h-12 bg-secondary/50 border-border rounded-none focus:border-primary"
                  required
                  minLength={8}
                  data-testid="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-xs uppercase tracking-wider text-muted-foreground">
                Passwort bestätigen
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Passwort wiederholen"
                  className="pl-12 pr-12 h-12 bg-secondary/50 border-border rounded-none focus:border-primary"
                  required
                  data-testid="confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {password && confirmPassword && password !== confirmPassword && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertTriangle size={16} />
                Passwörter stimmen nicht überein
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || password !== confirmPassword || password.length < 8}
              className="w-full h-12 bg-primary hover:bg-red-700 text-white uppercase tracking-widest font-bold text-sm rounded-none btn-glow transition-all duration-300 disabled:opacity-50"
              data-testid="setup-password-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Passwort speichern
                  <ArrowRight className="ml-2" size={18} />
                </>
              )}
            </Button>
          </form>
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">
            Swiss Historic Racing Team © {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
}
